import React, { useLayoutEffect, useRef, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";

interface TruncateTooltipProps {
    text: string;
    className?: string;
    /** 2 wraps onto a second line before cutting off. */
    lines?: 1 | 2;
}

export const TruncateTooltip: React.FC<TruncateTooltipProps> = ({
    text,
    className = "",
    lines = 1,
}) => {
    const spanRef = useRef<HTMLSpanElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const checkTruncate = () => {
        const span = spanRef.current;
        const container = containerRef.current;
        if (!span || !container) return;
        if (lines === 2) {
            setIsTruncated(span.scrollHeight > span.clientHeight + 1);
            return;
        }
        setIsTruncated(span.scrollWidth > container.clientWidth + 1);
    };

    useLayoutEffect(() => {
        checkTruncate();
        const span = spanRef.current;
        const container = containerRef.current;

        let ro: ResizeObserver | null = null;
        try {
            ro = new ResizeObserver(() => {
                checkTruncate();
            });
            if (span) ro.observe(span);
            if (container) ro.observe(container);
        } catch {
            window.addEventListener("resize", checkTruncate);
        }

        return () => {
            if (ro) {
                ro.disconnect();
            } else {
                window.removeEventListener("resize", checkTruncate);
            }
        };
    }, [text, lines]);

    const content = (
        <div ref={containerRef} className="w-full">
            <span
                ref={spanRef}
                // line-clamp sets its own display, so `block` would undo it.
                className={`${lines === 2 ? "line-clamp-2" : "block truncate"} cursor-default ${className}`}
                style={{ maxWidth: "100%" }}
            >
                {text}
            </span>
        </div>
    );

    if (!isTruncated) return content;

    return (
        <Tooltip>
            <TooltipTrigger asChild>{content}</TooltipTrigger>
            {/* Above dialogs (z-[110]–z-[130]) so it shows inside modals */}
            <TooltipContent className="z-[140]">
                <p className="max-w-sm break-words">{text}</p>
            </TooltipContent>
        </Tooltip>
    );
};
